import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TennisBall, Monitor, SlidersHorizontal, GameController, Play, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../hooks/useRealtime";

function teamName(t) { return t.p2 ? `${t.p1} / ${t.p2}` : t.p1; }

function ScoreMini({ match }) {
  const s = match.score;
  return (
    <div className="space-y-1">
      <div className={`flex items-center justify-between gap-3 ${s.winner === 1 ? "text-padel-blue" : ""}`}>
        <span className="font-body font-bold text-sm flex items-center gap-1 min-w-0 truncate">
          {s.winner === 1 && <Trophy size={14} weight="fill" className="shrink-0" />}{teamName(match.team1)}
        </span>
        <span className="font-heading tabular-nums text-base shrink-0">{s.sets1} · {s.games1} · <span className="text-padel-blue">{s.points1}</span></span>
      </div>
      <div className={`flex items-center justify-between gap-3 ${s.winner === 2 ? "text-padel-green" : ""}`}>
        <span className="font-body font-bold text-sm flex items-center gap-1 min-w-0 truncate">
          {s.winner === 2 && <Trophy size={14} weight="fill" className="shrink-0" />}{teamName(match.team2)}
        </span>
        <span className="font-heading tabular-nums text-base shrink-0">{s.sets2} · {s.games2} · <span className="text-padel-green">{s.points2}</span></span>
      </div>
    </div>
  );
}

// Public landing page reached by scanning a court QR code.
export default function CourtHub() {
  const { courtId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    api.get(`/courts/${courtId}/matches`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [courtId]);

  useEffect(() => { load(); }, [load]);

  useRealtime(useCallback((msg) => {
    if (msg.type === "refresh") return load();
    if (msg.type === "match_update" && msg.match && msg.match.court_id === courtId) load();
  }, [load, courtId]));

  const startMatch = async (id) => {
    if (!user) { toast.error("Inicia sesión para llevar el marcador"); navigate("/login"); return; }
    try {
      await api.post(`/matches/${id}/start`);
      navigate(`/control/${id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-muted-foreground font-heading uppercase tracking-widest">Cancha no encontrada</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-padel-blue font-heading uppercase animate-pulse-glow">Cargando…</div>;

  const { court, matches } = data;
  const liveMatch = matches.find((m) => m.status === "live");
  const scheduled = matches.filter((m) => m.status === "scheduled");

  return (
    <div className="min-h-screen grain flex flex-col items-center px-5 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md flex-1 flex flex-col"
      >
        <div className="flex items-center gap-2 mb-8 justify-center">
          <TennisBall size={28} weight="fill" className="text-padel-green" />
          <span className="font-heading text-xl font-extrabold uppercase tracking-widest">Padel Live</span>
        </div>

        <p className="text-xs tracking-[0.3em] uppercase text-padel-blue text-center mb-1">Cancha</p>
        <h1 className="font-heading text-5xl font-extrabold uppercase text-center mb-8" data-testid="hub-court-name">{court.name}</h1>

        {/* Live match */}
        {liveMatch && (
          <div className="bg-[#131417] border border-padel-green/50 glow-green p-5 mb-4" data-testid="hub-live-match">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 border text-padel-green border-padel-green animate-pulse-glow">En vivo</span>
            </div>
            <ScoreMini match={liveMatch} />
            <Link
              to={`/control/${liveMatch.id}`}
              data-testid="hub-control-link"
              className="mt-4 flex items-center justify-center gap-2 h-12 bg-padel-green text-black font-heading uppercase tracking-widest hover:bg-padel-blue transition-colors"
            >
              <GameController size={20} /> Llevar el marcador
            </Link>
          </div>
        )}

        {/* Scheduled matches with start option */}
        {scheduled.length > 0 && (
          <div className="mb-4">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-3">Partidos programados</p>
            <div className="space-y-3">
              {scheduled.map((m) => (
                <div key={m.id} data-testid={`hub-scheduled-${m.id}`} className="bg-[#131417] border border-white/10 p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-body font-bold text-sm truncate">{teamName(m.team1)}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">VS</span>
                    <span className="font-body font-bold text-sm truncate text-right">{teamName(m.team2)}</span>
                  </div>
                  <p className="text-[10px] tracking-widest uppercase text-muted-foreground flex items-center gap-1 mb-3">
                    <SlidersHorizontal size={11} /> {m.config.sets_to_win} sets · {m.config.games_per_set} games · {m.config.golden_point ? "punto de oro" : "ventaja"}
                  </p>
                  <button
                    data-testid={`hub-start-${m.id}`}
                    onClick={() => startMatch(m.id)}
                    className="w-full flex items-center justify-center gap-2 h-11 bg-padel-blue text-black font-heading uppercase tracking-widest hover:bg-padel-green transition-colors"
                  >
                    <Play size={18} weight="fill" /> Iniciar partido
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!liveMatch && scheduled.length === 0 && (
          <div className="bg-[#131417] border border-white/10 p-5 mb-4 text-center text-muted-foreground font-heading uppercase tracking-widest">
            Sin partidos en esta cancha
          </div>
        )}

        {/* actions pinned to bottom */}
        <div className="mt-auto pt-6 flex flex-col sm:flex-row gap-2">
          <Link
            to={`/court/${courtId}/screen`}
            data-testid="hub-screen-link"
            className="flex-1 flex items-center justify-center gap-2 h-16 bg-padel-blue/15 border border-padel-blue text-padel-blue font-heading text-sm uppercase tracking-widest hover:bg-padel-blue hover:text-black transition-colors"
          >
            <Monitor size={18} weight="fill" /> Pantalla completa
          </Link>
          <Link
            to="/"
            data-testid="hub-setup-link"
            className="flex-1 flex items-center justify-center gap-2 h-16 border border-white/15 text-muted-foreground font-heading text-sm uppercase tracking-widest hover:border-padel-green hover:text-padel-green transition-colors"
          >
            <SlidersHorizontal size={16} /> Configurar
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
